import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

export type OpeningDay =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface OpeningShift {
  start: string;
  end: string;
  user_ids?: number[];
}

export interface OpeningHourPayload {
  day: OpeningDay;
  is_open: boolean;
  is_24_hours?: boolean;
  is24hours?: boolean;
  shifts: OpeningShift[];
}

export interface SetOpeningSchedulePayload {
  schedule: OpeningHourPayload[];
}

export interface EmergencyOpeningHourOverridePayload {
  date: string; // YYYY-MM-DD
  is_open: boolean;
  is_24_hours?: boolean;
  is24hours?: boolean;
  shifts: OpeningShift[];
  reason?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PointOfSaleService {
  constructor(private http: HttpClient) {}

  getAllPOS(page: any): Observable<any> {
    return this.http.post(
      `${environment.Server_URL}/warehouses/all?page=${page}`,
      {}
    );
  }

  createPOS(formData: FormData): Observable<any> {
    return this.http.post(`${environment.Server_URL}/warehouses`, formData);
  }

  editPOS(formData: FormData, id: any): Observable<any> {
    return this.http.post(`${environment.Server_URL}/warehouses/${id}`, formData);
  }

  deletePOS(id: string): Observable<any> {
    return this.http.delete(`${environment.Server_URL}/warehouses/${id}`);
  }

  // Dashboard/internal warehouse details.
  getPOS(id: string): Observable<any> {
    return this.http.get(`${environment.Server_URL}/warehouses/${id}`);
  }

  // Public clinic page without login.
  getPublicClinic(id: string | number): Observable<any> {
    return this.http.get(`${environment.Server_URL}/public/clinics/${id}`);
  }

  getUserPOS(id: string): Observable<any> {
    return this.http.get(`${environment.Server_URL}/warehouses/user/${id}`);
  }

  getBusinessSettings(warehouseId: string | number): Observable<any> {
    return this.http.get(
      `${environment.Server_URL}/warehouses/${warehouseId}/business-settings`
    );
  }

  updateBusinessSettings(
    warehouseId: string | number,
    settings: {
      edit_price: boolean;
      quantityguard: boolean;
      has_delivery: boolean;
    }
  ): Observable<any> {
    return this.http.put(
      `${environment.Server_URL}/warehouses/${warehouseId}/business-settings`,
      settings
    );
  }

  // =========================
  // Warehouse Opening Hours
  // =========================

  getOpeningHours(warehouseId: string | number): Observable<any> {
    return this.http.get(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours`
    );
  }

  createOpeningHour(
    warehouseId: string | number,
    payload: OpeningHourPayload
  ): Observable<any> {
    return this.http.post(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours`,
      payload
    );
  }

  getOpeningHour(
    warehouseId: string | number,
    openingHourId: string | number
  ): Observable<any> {
    return this.http.get(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/${openingHourId}`
    );
  }

  updateOpeningHour(
    warehouseId: string | number,
    openingHourId: string | number,
    payload: OpeningHourPayload
  ): Observable<any> {
    return this.http.put(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/${openingHourId}`,
      payload
    );
  }

  patchOpeningHour(
    warehouseId: string | number,
    openingHourId: string | number,
    payload: Partial<OpeningHourPayload>
  ): Observable<any> {
    return this.http.patch(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/${openingHourId}`,
      payload
    );
  }

  deleteOpeningHour(
    warehouseId: string | number,
    openingHourId: string | number
  ): Observable<any> {
    return this.http.delete(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/${openingHourId}`
    );
  }

  setOpeningSchedule(
    warehouseId: string | number,
    payload: SetOpeningSchedulePayload
  ): Observable<any> {
    return this.http.put(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/set`,
      payload
    );
  }

  // =========================
  // Emergency Shift Override
  // =========================

  getEffectiveOpeningHour(
    warehouseId: string | number,
    date?: string
  ): Observable<any> {
    const url = `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/effective`;

    if (!date) {
      return this.http.get(url);
    }

    return this.http.get(url, {
      params: {
        date,
      },
    });
  }

  setEmergencyOpeningHourOverride(
    warehouseId: string | number,
    payload: EmergencyOpeningHourOverridePayload
  ): Observable<any> {
    return this.http.post(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/override`,
      payload
    );
  }

  deleteEmergencyOpeningHourOverride(
    warehouseId: string | number,
    date: string
  ): Observable<any> {
    return this.http.delete(
      `${environment.Server_URL}/warehouses/${warehouseId}/opening-hours/override/${encodeURIComponent(date)}`
    );
  }
}
